import React, {
  type ReactNode,
  lazy,
  Suspense,
  useState,
  useMemo,
  useEffect,
  useCallback,
  useContext,
} from 'react';
import type { AccountGridRow } from './accountsGridTypes';
import {
  buildAccountsExportTable,
  type AccountsExportColumnMetadata,
  type AccountsExportRowCandidate,
  type AccountsExportToolbarConfig,
  type AccountsSearchExportConfig,
} from './accountsExport';

// The Advisor-only search/export toolbar pulls in the `xlsx` writer through
// accountsExportEncoders. Lazy-loading it keeps that dependency out of the
// shared AccountsGrid bundle for Banker/Investor flows that never render it.
const AccountsDesktopGridToolbar = lazy(async () => {
  const module = await import('./accountsGridToolbarAdapters');
  return { default: module.AccountsDesktopGridToolbar };
});
const AccountsNativeGridToolbar = lazy(async () => {
  const module = await import('./accountsGridToolbarAdapters');
  return { default: module.AccountsNativeGridToolbar };
});
const AccountsXsGridToolbar = lazy(async () => {
  const module = await import('./accountsGridToolbarAdapters');
  return { default: module.AccountsXsGridToolbar };
});
import { formatNumberToCurrency } from '@/utils/currency';
import { formatAppDate, parseOptionalDate } from '@/utils/date';
import {
  Box,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha, lighten, type Theme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import AccountDetailsFlyout from '@/components/flyouts/AccountDetailsFlyout';
import { useLocalization } from '@/hooks/useLocalization';
import { useGetAccountsQuery } from '@/store/accountsApi';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { skipToken } from '@reduxjs/toolkit/query';
import { AccountBase } from '@/models/AccountViewModel';
import AccountActionsMenu from '../tables/AccountActionsMenu';
import { PAGE_SIZE_OPTIONS, PAGE_SIZES } from '@/constants/pagination';
import { GRID_KEYS } from '@/constants/gridKeys';
import { useGridColumns } from '@/hooks/useGridColumns';
import {
  groupingModelFor,
  deriveHeldBy,
  formatAccountTypeLabel,
  serverGroupKey,
  serverGroupLabel,
} from '@/constants/clientWealthGrouping';
import { AccountTypeNumber } from '@/utils/enums';
import {
  DataGridPremium,
  GridApiPremium,
  GridColDef,
  GridListViewColDef,
  GridRenderCellParams,
  useGridApiRef,
  GridFilterModel,
  GridRowGroupingModel,
  GridAggregationModel,
  GridSortModel,
  gridFilteredSortedRowIdsSelector,
  gridRowNodeSelector,
  gridRowGroupingModelSelector,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  getGridStringQuickFilterFn,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
} from '@mui/x-data-grid-premium';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import { ManagedColumnsManagement } from '@/components/table/ManagedColumnsManagement';
import { StandardWidgetProps } from '@/models/WidgetModels';
import {
  BALANCE_DESC_API_SORT,
  BALANCE_DESC_GRID_SORT_MODEL,
} from '@/constants/grid';
import { mobileWidth } from '@/constants';
import { useManagedColumnVisibility } from '@/hooks/useManagedColumnVisibility';
import { useAutoResizeGroupingColumn } from '@/hooks/useAutoResizeGroupingColumn';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import { AccountsDefaultGridToolbar } from './accountsDefaultGridToolbar';
import MobileExportButton from '@/components/MobileFilesExport';
import XsPreferencesDrawerToolbar, {
  type XsPreferencesDrawerToolbarProps,
} from '@/components/table/XsPreferencesDrawerToolbar';
import { Capacitor } from '@capacitor/core';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import { SyncErrorIcon } from '@/components/connectionError/SyncErrorIcon';
import { useAccountConnectionError } from '@/hooks/useAccountConnectionError';
import { ConnectionErrorModal } from '@/components/connectionError/ConnectionErrorModal';
import { HelpSupportContext } from '@/contexts/helpSupportContext';
import { actionColumnConfig } from '@/components/grids/gridColumnConfigs';
import { ContentContainerVariant } from '@/widgets/contentContainer';
import {
  GridContainer,
  type GridContainerSize,
} from '@/components/grids/GridContainer';

const NoAccountsRowsOverlay = ({ message }: { message?: ReactNode }) => {
  const { t } = useLocalization();
  return (
    <Box sx={{ width: '100%', p: 2 }}>
      {message ?? (
        <Typography>
          {t('noAccountsDataAvailable', 'No accounts data available.')}
        </Typography>
      )}
    </Box>
  );
};

const mobileDeviceUserAgentPattern = /Android|iPhone|iPad|iPod|Mobile/i;

const isMobileDeviceBrowser = () => {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent;
  const isIpadOsDesktopMode =
    /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1;

  return mobileDeviceUserAgentPattern.test(userAgent) || isIpadOsDesktopMode;
};

/**
 * Resolves the grid's loading flag. On the `filteredAccounts` path the internal
 * account query is skipped (its `isUninitialized` stays true forever), so loading
 * is driven by the page-supplied flag; otherwise the internal query's own status
 * is used. Grid-column config loading always counts.
 */
const resolveGridLoading = (
  hasFilteredAccounts: boolean,
  externalLoading: boolean,
  gridColumnsLoading: boolean,
  selfQueryLoading: boolean,
): boolean => {
  if (gridColumnsLoading) return true;
  if (hasFilteredAccounts) return externalLoading;
  return selfQueryLoading;
};

const openAccountUrl = (
  event: React.MouseEvent<HTMLAnchorElement>,
  accountUrl: string,
) => {
  event.preventDefault();
  event.stopPropagation();
  window.open(accountUrl, '_blank', 'noopener,noreferrer');
};

const brandedLinkColor = (theme: Theme) =>
  theme.palette.mode === 'dark'
    ? lighten(theme.palette.primary.main, 0.28)
    : theme.palette.primary.main;

const brandedLinkSx = {
  color: brandedLinkColor,
  textDecorationLine: 'underline',
  textDecorationThickness: '1px',
  textUnderlineOffset: '3px',
  textDecorationColor: (theme: Theme) => alpha(brandedLinkColor(theme), 0.45),
  '&:hover': {
    color: brandedLinkColor,
    textDecorationColor: brandedLinkColor,
  },
  '&:focus-visible': {
    borderRadius: 0.5,
    outline: (theme: Theme) =>
      `2px solid ${alpha(brandedLinkColor(theme), 0.55)}`,
    outlineOffset: '2px',
  },
};

const requiredAccountSearchFields: readonly string[] = [
  'Name',
  'AccountNumber',
];

interface AccountsGridColumnSnapshot {
  field: string;
  headerName?: string;
  disableExport?: boolean;
  groupable?: boolean;
}

const toAccountsExportColumnMetadata = (
  column: AccountsGridColumnSnapshot,
  visibilityModel: { [field: string]: boolean | undefined },
): AccountsExportColumnMetadata => ({
  field: column.field,
  headerName: column.headerName ?? column.field,
  visible: visibilityModel[column.field] ?? true,
  disableExport: column.disableExport ?? false,
  groupable: column.groupable ?? true,
  isAutogenerated: column.field.startsWith('__'),
});

/**
 * Field names the firm's saved column config uses, mapped to the field this grid
 * derives for the same column. Legacy stores Account Type as `ClassificationName`
 * and renders it from the classification pair; this grid derives that pair into
 * `formattedSubtype`.
 */
const CONFIG_FIELD_ALIASES: Record<string, string | undefined> = {
  ClassificationName: 'formattedSubtype',
};

const AccountsGridSyncError: React.FC<{ account: AccountBase }> = ({
  account,
}) => {
  const connectionError = useAccountConnectionError(account.InstitutionId);
  const [open, setOpen] = useState(false);
  const { handleOpen: openHelp } = useContext(HelpSupportContext);

  if (!connectionError) {
    return null;
  }

  return (
    <>
      <SyncErrorIcon
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      />
      <ConnectionErrorModal
        open={open}
        onClose={() => {
          setOpen(false);
        }}
        institutionName={account.InstitutionName}
        connectionError={connectionError}
        onContactSupport={() => {
          setOpen(false);
          openHelp(true, 'account connection');
        }}
      />
    </>
  );
};

// Rename component to be more generic since it's used in multiple places
/**
 * Renders a DataGrid to display a list of accounts with advanced features like
 * custom row grouping.
 *
 * This component handles two main functionalities:
 * 1. Displaying account data from either a direct `filteredAccounts` prop or by
 *    fetching via RTK Query.
 * 2. Allowing users to group rows by "Institution", "Data Source", or custom tags.
 *
 * @remarks
 * A significant workaround is implemented to handle a UI issue in MUI DataGridPremium
 * where enabling row grouping creates disabled, non-functional columns in the
 * "Columns" management panel. To solve this, a `useEffect` hook dynamically
 * injects and removes a `<style>` tag in the document's `<head>`. This style
 * globally hides the disabled items within the column management panel, which is
 * necessary because the panel is rendered in a React Portal, making component-scoped
 * CSS ineffective.
 *
 * @param {AccountsGridProps} props The component props.
 * @param {string} props.endDate The end date for the data.
 * @param {boolean} [props.isWidget=false] Renders the grid in a compact widget format.
 * @param {AccountBase[]} [props.filteredAccounts] An optional, pre-filtered list of accounts to display. If not provided, the component fetches its own data.
 * @param {string} [props.groupBy="none"] The field to group rows by. Can be 'institution', 'data source', or a custom tag field (e.g., 'investor:TagName').
 * @param {string[]} [props.excludedAccounts] Account IDs to exclude from the query.
 * @param {string[]} [props.excludedAccountTypes] Account types to exclude.
 * @param {string[]} [props.excludedCategories] Account categories to exclude.
 * @param {() => void} [props.onDataLoaded] Callback fired when data is loaded.
 * @returns {React.ReactElement} The rendered AccountsGrid component.
 */

interface AccountsGridProps extends StandardWidgetProps {
  endDate: string;
  useExternalScrollContainer?: boolean;
  filteredAccounts?: AccountBase[];
  groupBy?: string;
  /**
   * Show the "Held By" (owner) column by default. Advisor multi-client views
   * enable this so the flat, ungrouped list still shows which client owns each
   * account; single-investor widgets leave it hidden (redundant).
   */
  showHeldByColumn?: boolean;
  showHome?: boolean;
  showAway?: boolean;
  widgetCode?: string;
  allowedFields?: string[];
  defaultSortModel?: GridSortModel;
  emptyStateMessage?: ReactNode;
  handleEdit?: (account: AccountBase) => void;
  handleMarkAsClosed?: (account: AccountBase) => void;
  handleUploadHistory?: (account: AccountBase) => void;
  handleDelete?: (account: AccountBase) => void;
  handleAccountSelect?: (account: AccountBase) => void;
  excludedInstitutions?: number[];
  excludedTags?: number[];
  excludedGroups?: Record<string, string[]>;
  /** Page-supplied loading flag, used only on the `filteredAccounts` path. */
  loading?: boolean;
  /** tabsPanelKey forwarded to AccountDetailsFlyout (e.g. 'advisor-account'). */
  accountDetailTabsKey?: string;
  /** Investor scope forwarded to AccountDetailsFlyout for advisor-context tab queries. */
  investorId?: number;
  /** Advisor-only strict search and current-state export contract. */
  searchExportConfig?: AccountsSearchExportConfig;
  initialQuickFilterValues?: string[];
  onQuickFilterValuesChange?: (values: string[] | undefined) => void;
  useServerGrouping?: boolean;
  container?: ContentContainerVariant;
  /** Size passed through to the internal GridContainer. Defaults to 'md' (or the widget height when `isWidget`). */
  gridContainerSize?: GridContainerSize;
}

function gridGroupingModelFor(
  groupBy: string,
  useServerGrouping: boolean,
): string[] {
  if (!useServerGrouping) return groupingModelFor(groupBy);
  return groupBy === 'none' ? [] : ['ServerGroup'];
}

// The xs drawer toolbar's own export actions download through the browser,
// which the native WebView cannot do — it needs the Capacitor file flow. So the
// list view keeps its drawer for columns/sort/filters and takes the native
// export control in place of those actions.
function NativeXsPreferencesDrawerToolbar(
  props: XsPreferencesDrawerToolbarProps,
) {
  return (
    <XsPreferencesDrawerToolbar
      {...props}
      touchFriendly
      exportControl={
        <Box sx={{ '& .MuiButtonBase-root': { minHeight: 44, minWidth: 44 } }}>
          <MobileExportButton />
        </Box>
      }
    />
  );
}

const AccountsGrid: React.FC<AccountsGridProps> = ({
  endDate,
  isWidget = false,
  filteredAccounts,
  groupBy = 'none',
  showHeldByColumn = false,
  loading: loadingProp,
  accountDetailTabsKey,
  investorId,
  searchExportConfig,
  initialQuickFilterValues,
  onQuickFilterValuesChange,
  useServerGrouping = false,
  showHome,
  showAway,
  widgetCode,
  allowedFields,
  defaultSortModel = BALANCE_DESC_GRID_SORT_MODEL,
  emptyStateMessage,
  excludedAccounts,
  excludedAccountTypes,
  excludedCategories,
  excludedInstitutions,
  excludedTags,
  excludedGroups,
  onDataLoaded,
  handleEdit,
  handleMarkAsClosed,
  handleUploadHistory,
  handleDelete,
  handleAccountSelect: handleAccountSelectProp,
  gridContainerSize,
}) => {
  const hasActions =
    typeof handleEdit === 'function' &&
    typeof handleMarkAsClosed === 'function' &&
    typeof handleUploadHistory === 'function' &&
    typeof handleDelete === 'function';
  const [selectedAccount, setSelectedAccount] = useState<AccountBase | null>(
    null,
  );

  // Will be initialized later with forcedHiddenFields

  // State for dynamic grouping model
  const [groupingModel, setGroupingModel] = useState<GridRowGroupingModel>(() =>
    gridGroupingModelFor(groupBy, useServerGrouping),
  );

  // State for aggregation model - show sum of Balance on grouping rows
  const [aggregationModel, setAggregationModel] =
    useState<GridAggregationModel>({});

  const authLoaded = useAuthIsLoaded();
  const apiRef = useGridApiRef();
  const { t } = useLocalization();
  const theme = useTheme();
  const isMobile = useMediaQuery(mobileWidth);
  // Enable DataGridPremium list view only at XS sizes (sm and below)
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const { ref: widgetWidthRef, isBelow: isWidgetBelowXsWidth } =
    useWidgetWidthIsBelow(isWidget, theme.breakpoints.values.sm);
  const shouldUseListView = isXs || (isWidget && isWidgetBelowXsWidth);

  const isNative = Capacitor.getPlatform() !== 'web';
  const isMobileDevice = isNative || isMobileDeviceBrowser();

  // Load grid column config before data queries. Every consumer of this shared
  // grid — including the advisor clients-wealth accounts page — intentionally uses
  // this single config. Its rows do not all match the grid's derived field names,
  // though: Account Type is saved as `ClassificationName`, which CONFIG_FIELD_ALIASES
  // maps onto `formattedSubtype` below (EUD-888). The legacy `advisor-accounts`
  // config belonged to the now-retired bespoke table and is not loaded here.
  const { data: gridColumnsData, isLoading: gridColumnsLoading } =
    useGridColumns(GRID_KEYS.INVESTOR_ACCOUNTS);

  /**
   * Config rows arrive under the field names legacy used. Account Type is stored
   * as `ClassificationName`, but this grid derives that column into
   * `formattedSubtype`, so without the alias the row never matches and the
   * column is dropped (EUD-888).
   */
  const columnConfigRecords = useMemo(
    () =>
      (gridColumnsData?.Records ?? []).map((record) => ({
        ...record,
        GridColumnDataField:
          CONFIG_FIELD_ALIASES[record.GridColumnDataField] ??
          record.GridColumnDataField,
      })),
    [gridColumnsData],
  );

  const excludedGroupsApi = useMemo(() => {
    if (!excludedGroups || Object.keys(excludedGroups).length === 0)
      return undefined;
    return Object.entries(excludedGroups)
      .filter(([, ids]) => ids.length > 0)
      .map(([GroupId, ExcludedIds]) => ({ GroupId, ExcludedIds }));
  }, [excludedGroups]);

  // Use RTK Query to fetch accounts only if filteredAccounts is not provided
  const {
    data: rawAccountsData,
    isLoading,
    isError,
    isSuccess,
    isFetching,
    isUninitialized,
  } = useGetAccountsQuery(
    !filteredAccounts && authLoaded
      ? {
          showClosed: false,
          asOfDate: endDate,
          showHome,
          showAway,
          ExcludedAccounts: excludedAccounts,
          ExcludedAccountTypes: excludedAccountTypes,
          ExcludedCategories: excludedCategories,
          ...(widgetCode ? { WidgetCode: widgetCode } : {}),
          ...(excludedInstitutions && excludedInstitutions.length > 0
            ? { ExcludedInstitutions: excludedInstitutions }
            : {}),
          ...(excludedTags && excludedTags.length > 0
            ? { ExcludedTags: excludedTags }
            : {}),
          ...(excludedGroupsApi && excludedGroupsApi.length > 0
            ? { ExcludedGroups: excludedGroupsApi }
            : {}),
          sort: BALANCE_DESC_API_SORT,
        }
      : skipToken,
  );

  const accountsData = rawAccountsData;

  useEffect(() => {
    if (isSuccess && !isFetching && accountsData) {
      setTimeout(() => {
        onDataLoaded?.();
      }, 2000);
    }
  }, [accountsData, isSuccess, onDataLoaded, isFetching]);

  // Sync selectedAccount with latest cache data after refetch (e.g. after edit on mobile)
  useEffect(() => {
    if (!selectedAccount || !accountsData?.Records) return;

    const updated = accountsData.Records.find(
      (a: AccountBase) => a.AccountId === selectedAccount.AccountId,
    );
    if (updated && updated !== selectedAccount) {
      setSelectedAccount(updated);
    } else if (!updated) {
      setSelectedAccount(null);
    }
  }, [accountsData, selectedAccount]);

  // Update groupingModel + aggregation when groupBy prop changes
  useEffect(() => {
    const model = gridGroupingModelFor(groupBy, useServerGrouping);
    setGroupingModel(model);
    setAggregationModel(model.length ? { Balance: 'sum' } : {});
  }, [groupBy, useServerGrouping]);

  // On XS list view, changing grouping often requires a manual re-measure.
  // A window resize also triggers this, which is why it "fixes itself" after resizing.
  useEffect(() => {
    if (!isXs) return;
    // Wait a tick (or two) for the grouping model to be applied internally,
    // then force a re-measure. This mimics the "fix" you see after resizing the window.
    const raf1 = window.requestAnimationFrame(() => {
      const raf2 = window.requestAnimationFrame(() => {
        try {
          (
            apiRef.current as { updateDimensions?: () => void } | null
          )?.updateDimensions?.();
        } catch {
          // ignore
        }
        window.dispatchEvent(new Event('resize'));
      });

      // Cleanup inner RAF if outer was executed
      return () => {
        window.cancelAnimationFrame(raf2);
      };
    });

    // Also do a small delayed re-measure for slower devices / heavier DOM updates.
    const timeoutId = window.setTimeout(() => {
      try {
        (
          apiRef.current as { updateDimensions?: () => void } | null
        )?.updateDimensions?.();
      } catch {
        // ignore
      }
      window.dispatchEvent(new Event('resize'));
    }, 50);

    return () => {
      window.cancelAnimationFrame(raf1);
      window.clearTimeout(timeoutId);
    };
  }, [apiRef, isXs, groupBy, groupingModel]);

  // Prepare data for DataGrid
  const rows = useMemo(() => {
    // If filteredAccounts is provided, use that instead of accountsData

    const accounts: AccountBase[] =
      filteredAccounts || accountsData?.Records || [];

    const isCustomGroup =
      groupBy.startsWith('investor:') || groupBy.startsWith('advisor:');
    const mappedAccounts = accounts.map(
      (account: AccountBase, index: number): AccountGridRow => {
        let institutionName = account.InstitutionName;
        let dataSource = ''; // Initialize empty

        // Handle different account types. These derived values are the grouping
        // keys AND the displayed group/column labels, so they are localized (no
        // other code compares against the raw English strings).
        switch (account.AccountType) {
          case AccountTypeNumber.LINKED:
            dataSource = t('grid.dataSourceLinked', 'Linked Account');
            break;
          case AccountTypeNumber.MANUALLY_ENTERED:
            institutionName = t('grid.institutionManual', 'Manually entered');
            dataSource = t('grid.dataSourceManual', 'Manually Entered');
            break;
          case AccountTypeNumber.ESTATE:
            if (account.InstitutionName) {
              institutionName = t(
                'grid.institutionFromEstateSuffix',
                '{{name}} (from estate)',
                { name: account.InstitutionName },
              );
            } else {
              institutionName = t('grid.institutionFromEstate', 'from estate');
            }
            dataSource = t(
              'grid.dataSourceAdvisorProvided',
              'Provided by Advisor',
            );
            break;
          case AccountTypeNumber.ENTITY:
            institutionName = t('grid.institutionFromEstate', 'from estate');
            dataSource = t(
              'grid.dataSourceAdvisorProvided',
              'Provided by Advisor',
            );
            break;
          case AccountTypeNumber.ADVISOR_MANAGED:
            dataSource = t(
              'grid.dataSourceAdvisorManaged',
              'Managed By Advisor',
            );
            break;
          default:
            institutionName =
              account.InstitutionName ||
              t('grid.institutionNone', 'No Institution');
            break;
        }

        // Handle Zillow case
        if (account.UseZillow) {
          institutionName = t(
            'grid.institutionZillow',
            'Updated by Zillow Zestimate®',
          );
          dataSource = t(
            'grid.dataSourceAdvisorProvided',
            'Provided by Advisor',
          );
        }

        let customGroupName = '';
        if (isCustomGroup) {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- legacy account payloads can omit Tags despite the current model type.
          if (account.Tags && account.Tags[groupBy]) {
            customGroupName = account.Tags[groupBy];
          } else {
            customGroupName = t('grid.groupNotSet', 'Not set');
          }
        }

        let serverGroupName = '';
        if (useServerGrouping && groupBy !== 'none') {
          if (isCustomGroup) {
            serverGroupName = customGroupName;
          } else if (groupBy === 'IsHome') {
            serverGroupName = account.IsHome ? t('AUM') : t('HeldAway');
          } else if (groupBy === 'InstitutionId') {
            serverGroupName = serverGroupKey(
              account.InstitutionId,
              account.InstitutionName ||
                t('grid.institutionNone', 'No Institution'),
            );
          } else if (groupBy === 'ClassificationId') {
            serverGroupName = serverGroupKey(
              account.ClassificationId,
              account.ClassificationName || t('grid.groupNotSet', 'Not set'),
            );
          }
        }

        return {
          id: account.AccountId || index,
          ...account,
          InstitutionName: institutionName,
          DataSource: dataSource,
          CustomGroup: customGroupName,
          ServerGroup: serverGroupName,
          HeldBy: deriveHeldBy(
            account.Investors,
            t('grid.heldByUnassigned', 'Unassigned'),
          ),
          // EUD-888: reads as legacy does — "Investments / Rollover IRA".
          // DB rows can omit both classification fields, and the localized
          // "Not set" keeps the Account Type grouping from rendering a blank
          // group header for those.
          formattedSubtype: formatAccountTypeLabel(
            account.ClassificationName,
            account.SubClassificationName,
            t('grid.groupNotSet', 'Not set'),
          ),
        };
      },
    );

    if (isCustomGroup && !useServerGrouping) {
      mappedAccounts.sort((a: AccountGridRow, b: AccountGridRow) =>
        a.CustomGroup.localeCompare(b.CustomGroup),
      );
    }

    return mappedAccounts;
  }, [accountsData, filteredAccounts, groupBy, t, useServerGrouping]);

  const serverGroupOrder = useMemo(() => {
    const order = new Map<string, number>();
    if (!useServerGrouping) return order;
    for (const row of rows) {
      if (!order.has(row.ServerGroup)) order.set(row.ServerGroup, order.size);
    }
    return order;
  }, [rows, useServerGrouping]);

  const compareByServerGroupOrder = useCallback(
    (a: string | number | null, b: string | number | null) => {
      const rank = (value: string | number | null) =>
        serverGroupOrder.get(String(value ?? '')) ?? Number.MAX_SAFE_INTEGER;
      return rank(a) - rank(b);
    },
    [serverGroupOrder],
  );

  const [sortModel, setSortModel] = useState<GridSortModel>(defaultSortModel);
  useEffect(() => {
    if (!useServerGrouping) return;
    setSortModel(
      groupBy === 'none'
        ? defaultSortModel
        : [
            { field: GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD, sort: 'asc' },
            ...defaultSortModel,
          ],
    );
  }, [useServerGrouping, groupBy, defaultSortModel]);

  const total = useMemo(() => {
    const accounts: AccountBase[] =
      filteredAccounts || accountsData?.Records || [];

    return accounts.reduce(
      (acc: number, item: AccountBase) => acc + item.Balance,
      0,
    );
  }, [accountsData, filteredAccounts]);

  const [quickFilteredTotal, setQuickFilteredTotal] = useState<number | null>(
    null,
  );

  useEffect(() => {
    if (!searchExportConfig) {
      setQuickFilteredTotal(null);
      return;
    }

    const api = apiRef.current;
    if (!api?.subscribeEvent) {
      return;
    }

    const recomputeQuickFilteredTotal = () => {
      const rowIds = gridFilteredSortedRowIdsSelector(apiRef);
      let sum = 0;

      for (const rowId of rowIds) {
        const rowNode = gridRowNodeSelector(apiRef, rowId);
        if (rowNode.type !== 'leaf') {
          continue;
        }

        const row = api.getRow<AccountGridRow>(rowId);
        if (row) {
          sum += row.Balance;
        }
      }

      setQuickFilteredTotal(sum);
    };

    recomputeQuickFilteredTotal();
    return api.subscribeEvent('filteredRowsSet', recomputeQuickFilteredTotal);
  }, [apiRef, searchExportConfig, rows]);

  // `initialQuickFilterValues` only seeds `initialState`, which the grid reads
  // once. When the owning page's query params change while the grid stays
  // mounted (a link to the same route with a different `?search=`, or a history
  // move), the grid would keep filtering by its own stale values while the URL
  // — the source of truth — says otherwise. Push the change in instead.
  useEffect(() => {
    if (!initialQuickFilterValues) return;
    const api = apiRef.current;
    if (!api?.setQuickFilterValues) return;

    const current = api.state.filter.filterModel.quickFilterValues ?? [];
    if (
      current.length === initialQuickFilterValues.length &&
      current.every((value, index) => value === initialQuickFilterValues[index])
    ) {
      return;
    }
    api.setQuickFilterValues([...initialQuickFilterValues]);
  }, [apiRef, initialQuickFilterValues]);

  const displayedTotal = searchExportConfig
    ? (quickFilteredTotal ?? total)
    : total;

  const handleAccountSelect = useCallback(
    (account: AccountBase) => {
      setSelectedAccount(account);
      handleAccountSelectProp?.(account);
    },
    [handleAccountSelectProp],
  );

  const handleCloseModal = () => {
    setSelectedAccount(null);
  };

  const searchOnlyFields = useMemo(() => {
    if (!searchExportConfig) {
      return [];
    }

    const records = columnConfigRecords;
    const enabledFields = new Set(
      records
        .filter((record) => record.IsEnabled)
        .map((record) => record.GridColumnDataField),
    );
    const allowedFieldSet = allowedFields?.length
      ? new Set(allowedFields)
      : undefined;

    return requiredAccountSearchFields.filter(
      (field) =>
        (records.length > 0 && !enabledFields.has(field)) ||
        (allowedFieldSet !== undefined && !allowedFieldSet.has(field)),
    );
  }, [allowedFields, columnConfigRecords, searchExportConfig]);

  // Build columns dynamically from API config with safe fallbacks
  const rawColumns = useMemo(() => {
    const records = columnConfigRecords;
    const enabled = records.filter((record) => record.IsEnabled);
    const enabledSet = new Set(
      enabled.map((record) => record.GridColumnDataField),
    );
    const labelByField = new Map(
      enabled.map((record) => [record.GridColumnDataField, record.Label]),
    );
    const includedInExportByField = new Map(
      records.map((record) => [
        record.GridColumnDataField,
        record.IncludedInExport,
      ]),
    );
    const searchableFieldSet = new Set<string>(
      searchExportConfig?.searchableFields ?? [],
    );
    const applySearchExportContract = (column: GridColDef): GridColDef => {
      if (!searchExportConfig) {
        return column;
      }

      const includedInExport = includedInExportByField.get(column.field);

      return {
        ...column,
        ...(searchableFieldSet.has(column.field)
          ? { getApplyQuickFilterFn: getGridStringQuickFilterFn }
          : { getApplyQuickFilterFn: () => null }),
        ...(includedInExport === undefined
          ? {}
          : { disableExport: !includedInExport }),
      };
    };

    const defaultColumns: Record<string, GridColDef | undefined> = {
      CustomGroup: {
        field: 'CustomGroup',
        headerName: t('grid.group'),
        flex: isMobile ? 0 : 1,
        width: isMobile ? 120 : undefined,
        groupable: true,
        hideable: false,
      },
      ServerGroup: {
        field: 'ServerGroup',
        headerName: t('grid.group'),
        flex: isMobile ? 0 : 1,
        width: isMobile ? 120 : undefined,
        groupable: true,
        hideable: false,
        valueFormatter: (value: string) => serverGroupLabel(value),
      },
      InstitutionName: {
        field: 'InstitutionName',
        headerName: t('grid.institution'),
        flex: isMobile ? 0 : 1,
        width: isMobile ? 150 : undefined,
        groupable: true,
        hideable: false,
      },
      DataSource: {
        field: 'DataSource',
        headerName: t('grid.dataSource'),
        flex: isMobile ? 0 : 1,
        width: isMobile ? 130 : undefined,
        groupable: true,
        hideable: false,
      },
      HeldBy: {
        field: 'HeldBy',
        headerName: t('grid.heldBy', 'Held By'),
        flex: isMobile ? 0 : 1,
        width: isMobile ? 150 : undefined,
        groupable: true,
        hideable: showHeldByColumn,
      },
      Name: {
        field: 'Name',
        headerName: t('name'),
        flex: 1,
        minWidth: isMobile ? 180 : 200,
        renderCell: (params: GridRenderCellParams<AccountBase>) => {
          return (
            <Box
              sx={{
                lineHeight: 1.2,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              onClick={() => {
                handleAccountSelect(params.row);
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: 1.2,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {params.row.Name}
              </Typography>
              {groupBy === 'none' && !isMobile && (
                <Typography
                  variant='caption'
                  sx={{
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {params.row.InstitutionName}
                </Typography>
              )}
            </Box>
          );
        },
      },
      AccountNumber: {
        field: 'AccountNumber',
        headerName: t('grid.accountNumber'),
        flex: isMobile ? 0 : 1,
        width: isMobile ? 120 : undefined,
        renderCell: (params: GridRenderCellParams) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const accountNumber = String(params.row.AccountNumber ?? '').trim();
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          const accountUrl = String(params.row.AccountUrl ?? '').trim();

          if (!accountNumber) {
            return '';
          }

          if (isMobileDevice || !accountUrl) {
            return accountNumber;
          }

          return (
            <Link
              href={accountUrl}
              target='_blank'
              rel='noopener noreferrer'
              underline='hover'
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onClick={(event) => {
                openAccountUrl(event, accountUrl);
              }}
              sx={{
                minWidth: 0,
                textAlign: 'left',
                fontWeight: 600,
                ...brandedLinkSx,
              }}
            >
              {accountNumber}
            </Link>
          );
        },
      },
      Balance: {
        field: 'Balance',
        headerName: t('grid.balance'),
        type: 'number',
        valueFormatter: (value) => {
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (value === null || value === undefined) {
            return '';
          }
          return formatNumberToCurrency(value);
        },
        flex: isMobile ? 0 : 1,
        width: isMobile ? 120 : undefined,
        aggregable: true,
        renderCell: (params: GridRenderCellParams) => {
          // Handle aggregation values
          // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
          if (params.rowNode?.type === 'group') {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const aggregatedValue = params.value;
            if (aggregatedValue !== null && aggregatedValue !== undefined) {
              return (
                <Box
                  sx={{
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    height: '100%',
                    width: '100%',
                    textAlign: 'right',
                  }}
                >
                  {/* eslint-disable-next-line @typescript-eslint/no-unsafe-argument */}
                  {formatNumberToCurrency(aggregatedValue)}
                </Box>
              );
            }
          }
          // Regular cell rendering
          // eslint-disable-next-line @typescript-eslint/no-unsafe-return
          return params.formattedValue || '';
        },
        align: 'right',
        headerAlign: 'right',
      },
      formattedSubtype: {
        field: 'formattedSubtype',
        headerName: t('grid.accountType'),
        flex: isMobile ? 0 : 1,
        width: isMobile ? 120 : undefined,
        groupable: true,
      },
      LastUpdated: {
        field: 'LastUpdated',
        headerName: t('grid.lastUpdated'),
        type: 'date',
        valueGetter: (value: string) => parseOptionalDate(value),
        renderCell: (params: GridRenderCellParams<AccountBase, Date>) => (
          <Stack
            direction='row'
            alignItems='center'
            gap={0.5}
          >
            <span>
              {formatAppDate(params.value, {
                format: 'M/D/YYYY',
                fallback: '',
              })}
            </span>
            <AccountsGridSyncError account={params.row} />
          </Stack>
        ),
        flex: isMobile ? 0 : 1,
        width: isMobile ? 100 : undefined,
      },
    };

    const actionsCol: GridColDef = {
      field: 'actions',
      ...actionColumnConfig,
      width: isMobile ? 50 : 60,
      renderCell: (params: GridRenderCellParams) => {
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (params.rowNode?.type === 'group') {
          return null;
        }
        if (!hasActions) {
          return null;
        }
        return (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AccountActionsMenu
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              account={params.row}
              handleEdit={handleEdit}
              handleMarkAsClosed={handleMarkAsClosed}
              handleUploadHistory={handleUploadHistory}
              handleDelete={handleDelete}
            />
          </Box>
        );
      },
    };

    const buildFromConfig = (): GridColDef[] => {
      const groupingFields = gridGroupingModelFor(groupBy, useServerGrouping);
      const usesServerGroupColumn = groupingFields.includes('ServerGroup');

      // Stopgap (Andy): fixed base order matching main branch; ignore API order & return order
      const baseOrderedFields = [
        'CustomGroup',
        ...(usesServerGroupColumn ? (['ServerGroup'] as const) : []),
        'InstitutionName',
        'DataSource',
        'HeldBy',
        'Name',
        'AccountNumber',
        'Balance',
        'formattedSubtype',
        'LastUpdated',
        'actions',
      ];

      // Required columns for grouping and UX. HeldBy is always present but hidden
      // (mirrors CustomGroup); the active grouping source is force-included via
      // groupingModelFor so e.g. formattedSubtype exists when grouping by Account
      // Type without forcing that column visible for other consumers.
      // Legacy keeps a column the config says nothing about, and only hides one
      // a firm has actually turned off. Account Type is absent from some firms'
      // config entirely, so without this it would vanish here while legacy still
      // shows it (EUD-888).
      const configuredFields = new Set(
        records.map((record) => record.GridColumnDataField),
      );
      const unconfiguredFields = ['formattedSubtype'].filter(
        (field) => !configuredFields.has(field),
      );

      const requiredFields = new Set<string>([
        'CustomGroup',
        'InstitutionName',
        'DataSource',
        'HeldBy',
        'LastUpdated', // Always include Last Updated (not provided by useGridColumns)
        ...unconfiguredFields,
        ...groupingFields,
        ...(searchExportConfig ? requiredAccountSearchFields : []),
        ...(hasActions ? (['actions'] as const) : []),
      ]);

      const cols: GridColDef[] = [];

      for (const field of baseOrderedFields) {
        if (field === 'actions') {
          if (!hasActions) continue;
          cols.push(applySearchExportContract(actionsCol));
          continue;
        }
        const base = defaultColumns[field];
        if (!base) continue;
        const includeColumn =
          requiredFields.has(field) ||
          enabled.length === 0 ||
          enabledSet.has(field);
        if (includeColumn) {
          const label = labelByField.get(field);
          cols.push(
            applySearchExportContract({
              ...base,
              headerName: label || base.headerName,
            }),
          );
        }
      }

      // Append any extra enabled fields not in base order
      const extraEnabled = enabled
        .map((r) => r.GridColumnDataField)
        .filter((f) => !baseOrderedFields.includes(f));

      for (const field of extraEnabled) {
        const base = defaultColumns[field];
        const label = labelByField.get(field) || field;
        if (base) {
          cols.push(
            applySearchExportContract({
              ...base,
              headerName: label || base.headerName,
            }),
          );
        } else {
          cols.push(
            applySearchExportContract({
              field,
              headerName: label,
              flex: 1,
              minWidth: isMobile ? 120 : 150,
            }),
          );
        }
      }

      return cols;
    };

    let cols: GridColDef[];
    if (columnConfigRecords.length) {
      cols = buildFromConfig();
    } else {
      cols = [
        defaultColumns.CustomGroup,
        defaultColumns.InstitutionName,
        defaultColumns.DataSource,
        defaultColumns.HeldBy,
        defaultColumns.Name,
        defaultColumns.AccountNumber,
        defaultColumns.Balance,
        defaultColumns.formattedSubtype,
        defaultColumns.LastUpdated,
        ...(hasActions ? [actionsCol] : []),
      ]
        .filter((col): col is GridColDef => col !== undefined)
        .map(applySearchExportContract);
    }

    if (allowedFields?.length) {
      const allowedFieldSet = new Set(allowedFields);
      cols = cols.filter(
        (col) =>
          allowedFieldSet.has(col.field) ||
          (searchExportConfig &&
            requiredAccountSearchFields.includes(col.field)),
      );
    }

    if (isMobile) {
      cols = cols.filter((col) => col.field !== 'actions');
    }
    // Ensure actions column is always last
    const actionsIdx = cols.findIndex((c) => c.field === 'actions');
    if (actionsIdx >= 0 && actionsIdx !== cols.length - 1) {
      const [actionsColMove] = cols.splice(actionsIdx, 1);
      cols.push(actionsColMove);
    }
    // Ensure LastUpdated is immediately to the left of actions when present (desktop)
    if (!isMobile) {
      const lastUpdatedIdx = cols.findIndex((c) => c.field === 'LastUpdated');
      if (lastUpdatedIdx >= 0 && cols.length > 1) {
        const [lastUpdatedCol] = cols.splice(lastUpdatedIdx, 1);
        cols.splice(Math.max(cols.length - 1, 0), 0, lastUpdatedCol);
      }
    }

    // Defensive: DataGrid internals assume every entry is a valid column object.
    // If a consumer or config ever produces an undefined/null entry, the built-in
    // Columns/Filters panels can crash (e.g. reading `filterable` of undefined).
    return cols.filter(
      (c): c is GridColDef =>
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        !!c && typeof c.field === 'string',
    );
  }, [
    hasActions,
    columnConfigRecords,
    isMobileDevice,
    isMobile,
    groupBy,
    t,
    handleAccountSelect,
    handleDelete,
    handleEdit,
    handleMarkAsClosed,
    handleUploadHistory,
    allowedFields,
    showHeldByColumn,
    searchExportConfig,
    useServerGrouping,
  ]);

  const forcedHiddenFields = useMemo(() => {
    const fields = gridGroupingModelFor(groupBy, useServerGrouping);
    const withServerGroup = fields.includes('ServerGroup')
      ? fields
      : [...fields, 'ServerGroup'];
    return [...withServerGroup, ...searchOnlyFields];
  }, [groupBy, useServerGrouping, searchOnlyFields]);

  // HeldBy (owner) is hidden by default, but advisor multi-client views opt in
  // via `showHeldByColumn` so the flat, ungrouped list still shows which client
  // owns each account. When grouping by relationship, forcedHiddenFields hides
  // HeldBy regardless (it becomes the group), so there is no conflict.
  const defaultHiddenFields = useMemo(() => {
    const fields = ['InstitutionName', 'DataSource', 'CustomGroup'];
    if (!showHeldByColumn) fields.push('HeldBy');
    return fields;
  }, [showHeldByColumn]);

  const {
    columnVisibilityModel,
    handleColumnVisibilityChange,
    applyColumnVisibility,
    defaultColumnVisibilityModel,
  } = useManagedColumnVisibility<AccountBase>({
    hideDisabledColumnMenu: true,
    defaultHiddenFields,
    forcedHiddenFields,
  });

  const columns = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const next = applyColumnVisibility(rawColumns) ?? [];
    // Extra hardening: even if some upstream config/path ever produces
    // undefined/null entries, never pass them into the grid (it can crash
    // e.g. "cannot read properties of undefined (reading 'filterable')").
    return next.filter(
      (c): c is GridColDef<AccountBase> =>
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        !!c && typeof c.field === 'string',
    );
  }, [rawColumns, applyColumnVisibility]);

  const buildCurrentAccountsExportTable = useCallback(() => {
    const api = apiRef.current;
    if (!api) {
      throw new Error('Accounts grid is not ready for export.');
    }

    const rowCandidates: AccountsExportRowCandidate[] = [];
    const rowIds = gridFilteredSortedRowIdsSelector(apiRef);

    for (const rowId of rowIds) {
      const rowNode = gridRowNodeSelector(apiRef, rowId);
      if (rowNode.type !== 'leaf') {
        continue;
      }

      const row = api.getRow<AccountGridRow>(rowId);
      if (row) {
        rowCandidates.push({ type: 'leaf', row });
      }
    }

    const groupingModel = gridRowGroupingModelSelector(apiRef);
    const visibilityModel = gridColumnVisibilityModelSelector(apiRef);
    const columnMetadata = gridColumnDefinitionsSelector(apiRef).map((column) =>
      toAccountsExportColumnMetadata(column, visibilityModel),
    );

    for (const field of groupingModel) {
      const groupingColumn = api.getColumn(field);
      const groupingMetadata = toAccountsExportColumnMetadata(
        groupingColumn,
        visibilityModel,
      );
      const metadataIndex = columnMetadata.findIndex(
        (metadata) => metadata.field === field,
      );

      if (metadataIndex === -1) {
        columnMetadata.push(groupingMetadata);
      } else {
        columnMetadata[metadataIndex] = groupingMetadata;
      }
    }

    return buildAccountsExportTable({
      rowCandidates,
      columnMetadata,
      groupingModel,
      groupNotSet: t('grid.groupNotSet', 'Not set'),
    });
  }, [apiRef, t]);

  const accountsExportToolbarConfig = useMemo<
    AccountsExportToolbarConfig | undefined
  >(() => {
    if (!searchExportConfig) {
      return undefined;
    }

    return {
      buildTable: buildCurrentAccountsExportTable,
      filenamePrefix: searchExportConfig.filenamePrefix,
      uiDate: searchExportConfig.uiDate,
    };
  }, [buildCurrentAccountsExportTable, searchExportConfig]);

  const toolbarSlotProps = accountsExportToolbarConfig
    ? { accountsExportConfig: accountsExportToolbarConfig }
    : shouldUseListView
      ? { showQuickFilter: true }
      : {
          ...defaultToolbarExportSlotProps,
          showQuickFilter: true,
        };

  const listViewColDef: GridListViewColDef<AccountBase> = useMemo(
    () => ({
      field: 'listColumn',
      headerName: '',
      flex: 1,
      minWidth: 0,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        const { row, rowNode } = params;
        const visibilityModel: Partial<Record<string, boolean>> =
          params.api.state.columns.columnVisibilityModel;
        const allowedFieldSet = allowedFields?.length
          ? new Set(allowedFields)
          : null;
        const isFieldVisible = (field: string) =>
          (!allowedFieldSet || allowedFieldSet.has(field)) &&
          visibilityModel[field] !== false;

        // Group header row when grouping is enabled
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        if (rowNode?.type === 'group') {
          const rawGroupKey = String(
            // eslint-disable-next-line @typescript-eslint/no-base-to-string, @typescript-eslint/no-unnecessary-condition
            (rowNode as { groupingKey?: unknown })?.groupingKey ??
              t('grid.notGrouped', 'Not Grouped'),
          );
          const groupKey = serverGroupLabel(rawGroupKey);

          const descendantCountFromRowNode =
            typeof (rowNode as { descendantCount?: unknown })
              .descendantCount === 'number'
              ? ((rowNode as { descendantCount?: number }).descendantCount ?? 0)
              : 0;

          const childrenCountFromRowNode = Array.isArray(
            (rowNode as { children?: unknown }).children,
          )
            ? ((rowNode as { children?: unknown[] }).children?.length ?? 0)
            : Array.isArray((rowNode as { childrenIds?: unknown }).childrenIds)
              ? ((rowNode as { childrenIds?: unknown[] }).childrenIds?.length ??
                0)
              : 0;

          // eslint-disable-next-line @typescript-eslint/no-deprecated
          const node = (params.api as GridApiPremium).getRowNode(params.id);
          const descendantCountFromApi =
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            typeof (node as { descendantCount?: unknown })?.descendantCount ===
            'number'
              ? ((node as { descendantCount?: number }).descendantCount ?? 0)
              : 0;

          const groupCount =
            descendantCountFromRowNode ||
            childrenCountFromRowNode ||
            descendantCountFromApi ||
            0;

          const isExpanded = !!(
            rowNode as unknown as {
              childrenExpanded?: boolean;
            }
          ).childrenExpanded;

          const handleToggleExpand = (event: React.MouseEvent) => {
            event.stopPropagation();
            event.preventDefault();
            (params.api as GridApiPremium).setRowChildrenExpansion(
              params.id,
              !isExpanded,
            );
          };

          const totalBalance = (
            rowNode as unknown as {
              aggregation?: { Balance?: { sum?: number } };
            }
          ).aggregation?.Balance?.sum;

          return (
            <Box
              sx={{
                width: '100%',
                p: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <Box
                  onClick={handleToggleExpand}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    p: 0.25,
                    borderRadius: 1,
                    '&:hover': {
                      backgroundColor: (theme) => theme.palette.action.hover,
                    },
                  }}
                  aria-label={
                    isExpanded
                      ? t('grid.collapseGroup', 'Collapse group')
                      : t('grid.expandGroup', 'Expand group')
                  }
                >
                  {isExpanded ? (
                    <ExpandMoreIcon fontSize='small' />
                  ) : (
                    <ChevronRightIcon fontSize='small' />
                  )}
                </Box>

                <Typography
                  variant='body1'
                  sx={{
                    fontWeight: '600 !important',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {/* eslint-disable-next-line @typescript-eslint/restrict-template-expressions */}
                  {groupCount > 0 ? `${groupKey} (${groupCount})` : groupKey}
                </Typography>
              </Box>

              {totalBalance != null ? (
                <Typography
                  variant='body1'
                  sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                >
                  {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
                  {formatNumberToCurrency(Number(totalBalance))}
                </Typography>
              ) : null}
            </Box>
          );
        }

        const lastUpdatedValue = formatAppDate(row.LastUpdated, {
          format: 'M/D/YYYY',
          fallback: '',
        });

        // EUD-888: the same value the desktop column shows, so a card and a row
        // never disagree about an account's type.
        const accountType =
          'formattedSubtype' in row && typeof row.formattedSubtype === 'string'
            ? row.formattedSubtype
            : '';
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
        const accountNumber = String(row.AccountNumber ?? '');
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion
        const accountUrl = String(row.AccountUrl ?? '').trim();

        return (
          <Box
            sx={{
              width: '100%',
              pl: 0,
              pr: 0,
              pt: 3,
              pb: 3,
              display: 'flex',
              alignItems: 'flex-start',
              gap: 0.5,
              cursor: isMobileDevice ? 'pointer' : 'default',
            }}
            onClick={
              isMobileDevice
                ? () => {
                    handleAccountSelect(row);
                  }
                : undefined
            }
          >
            {hasActions ? (
              <Box
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.stopPropagation();
                }}
                sx={{ flex: '0 0 auto', mt: -1, ml: -1 }}
              >
                <AccountActionsMenu
                  account={row}
                  handleEdit={handleEdit}
                  handleMarkAsClosed={handleMarkAsClosed}
                  handleUploadHistory={handleUploadHistory}
                  handleDelete={handleDelete}
                />
              </Box>
            ) : null}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Line 1: Name (left) + Balance (right) */}
              {(isFieldVisible('Name') || isFieldVisible('Balance')) && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 2,
                    mb: 0,
                  }}
                >
                  <Typography
                    variant='body1'
                    component='button'
                    type='button'
                    onClick={(event) => {
                      event.stopPropagation();
                      handleAccountSelect(row);
                    }}
                    sx={{
                      p: 0,
                      border: 0,
                      background: 'transparent',
                      cursor: 'pointer',
                      font: 'inherit',
                      fontWeight: '600 !important',
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      textAlign: 'left',
                      ...brandedLinkSx,
                      textDecorationLine: 'none',
                      '&:hover': {
                        ...brandedLinkSx['&:hover'],
                        textDecorationLine: 'underline',
                      },
                    }}
                  >
                    {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition */}
                    {isFieldVisible('Name') ? (row.Name ?? '') : ''}
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{
                      fontWeight: '600 !important',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isFieldVisible('Balance')
                      ? formatNumberToCurrency(row.Balance)
                      : ''}
                  </Typography>
                </Box>
              )}

              {/* Lines 2-5: value-only */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {isFieldVisible('InstitutionName') ? (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ fontWeight: 400, textAlign: 'left' }}
                  >
                    {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion, @typescript-eslint/no-unnecessary-condition */}
                    {String(row.InstitutionName ?? '')}
                  </Typography>
                ) : null}

                {isFieldVisible('AccountNumber') ? (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ fontWeight: 400, textAlign: 'left' }}
                  >
                    {`${t('grid.acctNumber', 'Acct #:')} `}
                    {!isMobileDevice && accountUrl ? (
                      <Link
                        href={accountUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        underline='hover'
                        onMouseDown={(event) => {
                          event.stopPropagation();
                        }}
                        onClick={(event) => {
                          openAccountUrl(event, accountUrl);
                        }}
                        sx={{
                          fontWeight: 600,
                          ...brandedLinkSx,
                        }}
                      >
                        {accountNumber}
                      </Link>
                    ) : (
                      accountNumber
                    )}
                  </Typography>
                ) : null}

                {isFieldVisible('HeldBy') ? (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ fontWeight: 400, textAlign: 'left' }}
                  >
                    {`${t('grid.heldBy', 'Held By')}: ${deriveHeldBy(
                      row.Investors,
                      t('grid.heldByUnassigned', 'Unassigned'),
                    )}`}
                  </Typography>
                ) : null}

                {isFieldVisible('formattedSubtype') ? (
                  <Typography
                    variant='body2'
                    color='text.secondary'
                    sx={{ fontWeight: 400, textAlign: 'left' }}
                  >
                    {`${t('grid.type', 'Type')}: ${accountType}`}
                  </Typography>
                ) : null}

                {isFieldVisible('LastUpdated') ? (
                  <Stack
                    direction='row'
                    alignItems='center'
                    gap={0.5}
                  >
                    <Typography
                      variant='body2'
                      color='text.secondary'
                      sx={{ fontWeight: 400, textAlign: 'left' }}
                    >
                      {`${t('grid.updated', 'Updated')} ${lastUpdatedValue}`}
                    </Typography>
                    <AccountsGridSyncError account={row} />
                  </Stack>
                ) : null}
              </Box>
            </Box>
          </Box>
        );
      },
    }),
    [
      allowedFields,
      handleAccountSelect,
      handleDelete,
      handleEdit,
      handleMarkAsClosed,
      handleUploadHistory,
      hasActions,
      isMobileDevice,
      t,
    ],
  );

  // Auto-sizing the first (grouping) column is helpful on desktop, but on XS list view
  // it can lock the group header rows to a narrow fixed width until the grid is resized.
  // Disable it on XS so list view can reliably use full width.
  useAutoResizeGroupingColumn({
    apiRef,
    columnsForDisplay: columns,
    rowGroupingModel: shouldUseListView ? [] : groupingModel,
  });

  if (isError && !filteredAccounts) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Typography color='error'>
          {t('errorLoadingAccountsData', 'Error loading accounts data.')}
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box ref={widgetWidthRef}>
        <GridContainer
          isWidget={isWidget}
          size={gridContainerSize ?? 'md'}
        >
          <Suspense fallback={null}>
            <DataGridPremium
              // On XS list view, switching grouping can leave the grid with stale internal width
              // calculations until a resize occurs. Forcing a remount on groupBy changes fixes this.
              key={
                shouldUseListView
                  ? `accounts-grid-xs-${groupBy}-${groupingModel.join('|')}`
                  : 'accounts-grid'
              }
              rows={rows}
              columns={columns}
              apiRef={apiRef}
              disableRowSelectionOnClick
              showToolbar
              disablePivoting
              pagination
              pageSizeOptions={[...PAGE_SIZE_OPTIONS.STANDARD]}
              listView={shouldUseListView}
              {...(shouldUseListView ? { listViewColumn: listViewColDef } : {})}
              {...(shouldUseListView
                ? { getRowHeight: () => 'auto' as const }
                : {})}
              slotProps={{
                loadingOverlay: {
                  variant: 'linear-progress',
                  noRowsVariant: 'skeleton',
                },
                noRowsOverlay: {
                  sx: {
                    width: '100%',
                    p: 2,
                  },
                  children: (
                    <NoAccountsRowsOverlay message={emptyStateMessage} />
                  ),
                },
                toolbar: toolbarSlotProps,
                columnsManagement: {
                  defaultColumnVisibilityModel,
                  getTogglableColumns: (cols: GridColDef[]) =>
                    cols
                      .filter((c) => c.hideable !== false)
                      .map((c) => c.field)
                      .filter(
                        (field) =>
                          field !== 'actions' &&
                          field !== 'listColumn' &&
                          !field.startsWith('__'),
                      ),
                },
              }}
              slots={{
                columnsManagement: ManagedColumnsManagement,
                ...(searchExportConfig
                  ? shouldUseListView
                    ? { toolbar: AccountsXsGridToolbar }
                    : isNative
                      ? { toolbar: AccountsNativeGridToolbar }
                      : { toolbar: AccountsDesktopGridToolbar }
                  : shouldUseListView
                    ? isNative
                      ? { toolbar: NativeXsPreferencesDrawerToolbar }
                      : { toolbar: XsPreferencesDrawerToolbar }
                    : isNative
                      ? { toolbar: CustomToolbarForNative }
                      : { toolbar: AccountsDefaultGridToolbar }),
              }}
              groupingColDef={{
                hideDescendantCount: false,
                width: undefined, // Let it auto-size
                flex: 1,
                minWidth: 200,
                ...(searchExportConfig
                  ? { getApplyQuickFilterFn: () => null }
                  : {}),
                ...(useServerGrouping
                  ? { sortComparator: compareByServerGroupOrder }
                  : {}),
              }}
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={handleColumnVisibilityChange}
              {...(onQuickFilterValuesChange
                ? {
                    onFilterModelChange: (model: GridFilterModel) => {
                      onQuickFilterValuesChange(model.quickFilterValues);
                    },
                  }
                : {})}
              rowGroupingModel={groupingModel}
              onRowGroupingModelChange={setGroupingModel}
              {...(useServerGrouping
                ? { sortModel, onSortModelChange: setSortModel }
                : {})}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: PAGE_SIZES.DEFAULT },
                },
                sorting: {
                  sortModel: defaultSortModel,
                },
                ...(searchExportConfig
                  ? {
                      filter: {
                        filterModel: {
                          items: [],
                          quickFilterValues: initialQuickFilterValues ?? [],
                          quickFilterExcludeHiddenColumns: false,
                        },
                      },
                    }
                  : {}),
              }}
              defaultGroupingExpansionDepth={0}
              aggregationModel={aggregationModel}
              onAggregationModelChange={setAggregationModel}
              getAggregationPosition={(groupNode) => {
                // Show aggregates on the same row as the group header
                // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                return groupNode ? 'inline' : null;
              }}
              aggregationRowsScope={searchExportConfig ? 'filtered' : 'all'}
              loading={resolveGridLoading(
                Boolean(filteredAccounts),
                Boolean(loadingProp),
                gridColumnsLoading,
                isLoading || isFetching || isUninitialized,
              )}
              sx={{
                // Hide the aggregation function name (e.g., "sum") from column headers
                "& .MuiDataGrid-columnHeader[data-field='Balance'] .MuiDataGrid-columnHeaderTitleContainer .MuiDataGrid-columnHeaderTitleContainerContent > span:last-child":
                  {
                    display: 'none',
                  },
                // Alternative selector for aggregation label
                "& .MuiDataGrid-columnHeader[data-field='Balance'] .MuiDataGrid-aggregationColumnHeaderLabel":
                  {
                    display: 'none',
                  },
                // Make Balance column header bold
                '& .MuiDataGrid-columnHeader .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 600,
                },
                ...(shouldUseListView
                  ? {
                      '& .MuiDataGrid-row:not(.MuiDataGrid-row--group) .MuiDataGrid-cellEmpty':
                        {
                          display: 'none !important',
                        },
                      // In list view, force the single list column to actually fill the grid width.
                      '& .MuiDataGrid-main': {
                        width: '100%',
                      },
                      '& .MuiDataGrid-virtualScroller': {
                        width: '100%',
                      },
                      // MUI often sizes this to the total columns width; in list view we want 100%.
                      '& .MuiDataGrid-virtualScrollerRenderZone': {
                        width: '100% !important',
                        minWidth: '100% !important',
                        maxWidth: '100% !important',
                      },
                      '& .MuiDataGrid-virtualScrollerContent': {
                        width: '100% !important',
                      },
                      '& .MuiDataGrid-virtualScrollerContent > div': {
                        width: '100% !important',
                        maxWidth: '100% !important',
                      },
                      '& .MuiDataGrid-row': {
                        display: 'flex',
                        flexWrap: 'nowrap',
                        width: '100% !important',
                        minWidth: '100% !important',
                        maxWidth: '100% !important',
                      },
                      '& .MuiDataGrid-cell': {
                        display: 'flex',
                        flexWrap: 'nowrap',
                        width: '100% !important',
                        minWidth: '100% !important',
                        maxWidth: '100% !important',
                        flex: '1 1 0',
                      },
                      '& .MuiDataGrid-cellContent': {
                        width: '100%',
                        flex: 1,
                      },
                    }
                  : {}),
              }}
            />
          </Suspense>
        </GridContainer>

        <Box
          sx={{
            mt: 2,
            fontWeight: 600,
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant='subtitle1'>{t('total', 'Total')}</Typography>
          <Typography
            variant='subtitle1'
            data-testid='accounts-grid-total'
          >
            {formatNumberToCurrency(displayedTotal)}
          </Typography>
        </Box>

        {selectedAccount && (
          <AccountDetailsFlyout
            open={!!selectedAccount}
            toggleDrawer={handleCloseModal}
            account={selectedAccount}
            tabsPanelKey={accountDetailTabsKey}
            investorId={
              investorId ?? selectedAccount.Investors?.[0]?.InvestorId
            }
            onDelete={handleDelete}
            onEdit={handleEdit}
            onMarkAsClosed={handleMarkAsClosed}
          />
        )}
      </Box>
    </>
  );
};

// Export with new name but keep old name as alias for backward compatibility
export { AccountsGrid as PortfolioAccountsTable };
export default AccountsGrid;
